import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { EmptySearch, loaderSVG } from "../../../../assets/images";
import { NO_RESULTS_FOUND } from "../../../processENV";
import { fetchGlobalSearch, GLOBAL_SEARCH_MESSAGE_CAP, GLOBAL_SEARCH_TYPE } from "../../../../api/globalSearch";
import GlobalSearchResultItem from "./GlobalSearchResultItem";
import {
    filterAvailableResults,
    openChatFromSearchResult,
    openMessageFromSearchResult,
    prefetchDisplayNames
} from "./globalSearchNavigation";
import "./GlobalSearch.scss";

const TABS = [
    { key: "messages", label: "Messages", type: GLOBAL_SEARCH_TYPE.MESSAGES },
    { key: "chats", label: "Recent Chats", type: GLOBAL_SEARCH_TYPE.RECENTS },
    { key: "groups", label: "Groups", type: GLOBAL_SEARCH_TYPE.GROUPS },
    { key: "contacts", label: "Contacts", type: GLOBAL_SEARCH_TYPE.CONTACTS }
];

/** Selected tab on opening search — independent of the display order above. */
const DEFAULT_TAB = "messages";

const DEBOUNCE_MS = 600;
const CAP_NOTICE = `Showing the most recent ${GLOBAL_SEARCH_MESSAGE_CAP} matches.`;

/** Identity of a result row, used to drop duplicates a repeated page would introduce. */
const getResultKey = (item = {}) =>
    item.messageId || item.username || item.groupId || item.userId || item.jid || JSON.stringify(item);

const GlobalSearch = ({ searchTerm, handleConversationSectionDisplay }) => {
    const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
    // Start empty so the first term also waits out the debounce — otherwise the panel,
    // which mounts on the first keystroke, would fire an immediate un-debounced request.
    const [debouncedTerm, setDebouncedTerm] = useState("");
    const [results, setResults] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [capReached, setCapReached] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    // Pagination cursors for the tab currently shown: page for recents/contacts/groups,
    // cursor (last message id) for messages.
    const pageRef = useRef(1);
    const cursorRef = useRef(null);
    const requestIdRef = useRef(0);
    // Mirrors of the paging state, read synchronously so a rapid second click cannot
    // fire a duplicate request before React has re-rendered.
    const loadingMoreRef = useRef(false);
    const hasMoreRef = useRef(false);
    const resultCountRef = useRef(0);
    const resultsRef = useRef([]);

    const setHasMoreSynced = useCallback((value) => {
        hasMoreRef.current = value;
        setHasMore(value);
    }, []);

    const activeType = TABS.find((tab) => tab.key === activeTab)?.type;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTerm(searchTerm), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const reqId = ++requestIdRef.current;
        const term = (debouncedTerm || "").trim();
        pageRef.current = 1;
        cursorRef.current = null;

        loadingMoreRef.current = false;
        resultCountRef.current = 0;
        resultsRef.current = [];
        setLoadingMore(false);
        setCapReached(false);

        if (!term) {
            setResults([]);
            setHasMoreSynced(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        fetchGlobalSearch({ searchText: term, type: activeType, page: 1, cursor: null, loadedCount: 0 })
            .then(async (res) => {
                if (reqId !== requestIdRef.current) return null;
                // Resolve names before painting, so a row never flashes a placeholder
                // and then swaps to the real name.
                await prefetchDisplayNames(res.items, activeType);
                return res;
            })
            .then((res) => {
                if (!res || reqId !== requestIdRef.current) return;
                // Drop groups the user has left — they render as a raw id and can't be opened.
                const items = filterAvailableResults(res.items, activeType);
                setResults(items);
                resultsRef.current = items;
                resultCountRef.current = items.length;
                setHasMoreSynced(res.hasMore);
                setCapReached(res.capReached);
                // Page 1 has just been consumed, so the next request is page 2.
                pageRef.current = res.nextPage || 2;
                cursorRef.current = res.nextCursor;
                setLoading(false);
            });
    }, [debouncedTerm, activeType, setHasMoreSynced]);

    const handleLoadMore = useCallback(async () => {
        const reqId = requestIdRef.current;
        const term = (debouncedTerm || "").trim();
        // Guard on refs, not state — the observer may fire again before React re-renders.
        if (!term || loadingMoreRef.current || !hasMoreRef.current) return;

        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const res = await fetchGlobalSearch({
                searchText: term,
                type: activeType,
                page: pageRef.current,
                cursor: cursorRef.current,
                loadedCount: resultCountRef.current
            });
            // The tab or the term changed while this page was in flight — the newer request owns the state.
            if (reqId !== requestIdRef.current) return;

            await prefetchDisplayNames(res.items, activeType);
            if (reqId !== requestIdRef.current) return;

            // Computed outside the state updater so it stays free of side effects.
            const available = filterAvailableResults(res.items, activeType);
            const seen = new Set(resultsRef.current.map(getResultKey));
            const fresh = available.filter((item) => !seen.has(getResultKey(item)));
            if (fresh.length) {
                const merged = [...resultsRef.current, ...fresh];
                resultsRef.current = merged;
                resultCountRef.current = merged.length;
                setResults(merged);
            }

            // A page that returns nothing new means the cursor/page did not advance
            // server-side; stop rather than requesting the same page forever.
            const advanced = fresh.length > 0;
            if (!advanced) {
                console.warn(
                    `[globalSearch] type="${activeType}" returned no new results for page=${pageRef.current} cursor=${cursorRef.current}; stopping pagination.`
                );
            }
            setHasMoreSynced(res.hasMore && advanced);
            setCapReached(res.capReached);
            // Always advance — falling back to the current page would refetch it forever.
            pageRef.current = res.nextPage || pageRef.current + 1;
            cursorRef.current = res.nextCursor;
        } finally {
            if (reqId === requestIdRef.current) setLoadingMore(false);
            loadingMoreRef.current = false;
        }
    }, [debouncedTerm, activeType, setHasMoreSynced]);

    const handleResultClick = useCallback(
        (item) => {
            if (activeType === GLOBAL_SEARCH_TYPE.MESSAGES) {
                openMessageFromSearchResult(item);
                return;
            }
            openChatFromSearchResult(item, activeType, handleConversationSectionDisplay);
        },
        [activeType, handleConversationSectionDisplay]
    );

    const showEmptyState = !loading && results.length === 0 && Boolean((debouncedTerm || "").trim());

    return (
        <div className="global-search">
            <div className="global-search-tabs" role="tablist">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`global-search-tab ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* `chat-list` keeps the shared sidebar row styling that is scoped to it */}
            <div className="chat-list global-search-results">
                {loading && (
                    <div className="search-loader-container">
                        <img src={loaderSVG} alt="searching" className="search-loader" />
                    </div>
                )}

                {!loading && results.length > 0 && (
                    <ul className="chat-list-ul global-search-list">
                        {results.map((item, index) => (
                            <GlobalSearchResultItem
                                key={`${getResultKey(item)}-${index}`}
                                item={item}
                                type={activeType}
                                searchTerm={debouncedTerm}
                                onClick={handleResultClick}
                            />
                        ))}
                    </ul>
                )}

                {/* Paging is explicit: the next page loads only on click. */}
                {!loading && hasMore && (
                    <div className="global-search-load-more-container">
                        <button
                            type="button"
                            className="global-search-load-more"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? "Loading..." : "Load more"}
                        </button>
                    </div>
                )}

                {/* Shown only when the cap truncated the results, not on reaching the end of a smaller set. */}
                {!loading && capReached && (
                    <div className="global-search-cap-notice">{CAP_NOTICE}</div>
                )}

                {showEmptyState && (
                    <div className="no-search-record-found">
                        <div className="norecent-chat">
                            <i className="norecent-chat-img">
                                <EmptySearch />
                            </i>
                            <h4>{NO_RESULTS_FOUND}</h4>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

GlobalSearch.propTypes = {
    searchTerm: PropTypes.string,
    handleConversationSectionDisplay: PropTypes.func
};

export default GlobalSearch;
